/**
 * Capability linkage templates (Feature 71), the first tab of the platform's
 * Automation (Feature 74 D2). A row opens the template in the record panel;
 * New linkage and the panel's Open canvas lead to the editor. A template
 * stored before gates existed may hold empty gates, which a run skips; a
 * banner says so before anything runs, and the Gates column names which
 * (Feature 72 D4). Runs names what the template runs, and Overlaps a
 * template of either kind that would wake on the same capability and run the
 * same one (Feature 74 D5, D11).
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { listLinkageTemplates } from '@optum/api-client/endpoints/linkage-templates';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { viewerTimeZone } from '../../lib/cron.js';
import { qk } from '../../lib/query-keys.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { DataTable } from '../../ui/DataTable.js';
import { Badge, Banner, Icon, Inline } from '../../ui/finixt.js';
import { StatRow } from '../../ui/StatRow.js';
import { OverlapBadge } from '../automation/OverlapBadge.js';
import { NEW_LINKAGE_PATH } from '../automation/paths.js';
import { usePlatformOverlaps } from '../automation/usePlatformOverlaps.js';
import { isCall, isSchedule, startWords } from './start.js';

/** Plural agreement for a count in a cell. */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** The Automation header's action while the Linkages tab is showing. */
export function NewLinkageAction() {
  const navigate = useNavigate();
  return (
    <Button variant="primary" icon="add" onClick={() => navigate(NEW_LINKAGE_PATH)}>
      New linkage
    </Button>
  );
}

export function LinkagesTab() {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const overlapsOf = usePlatformOverlaps();
  const timeZone = viewerTimeZone();
  const templates = useQuery({ queryKey: qk.linkageTemplates(), queryFn: listLinkageTemplates });
  const rows = templates.data ?? [];
  const enabled = rows.reduce((total, t) => total + t.enabled_organizations, 0);
  const withEmpty = rows.filter((t) => (t.empty_gates?.length ?? 0) > 0).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {templates.isPending ? (
        <LoadingBlock />
      ) : templates.isError ? (
        <ErrorBlock
          message={templates.error instanceof Error ? templates.error.message : 'Failed to load'}
        />
      ) : rows.length === 0 ? (
        <EmptyBlock>
          No linkages yet. Use <strong>New linkage</strong> above to draw the first one.
        </EmptyBlock>
      ) : (
        <>
          {withEmpty > 0 && (
            <Banner
              tone="warn"
              title={`${count(withEmpty, 'template')} ${withEmpty === 1 ? 'has' : 'have'} an empty gate`}
            >
              The step behind it is skipped <span className="mono">gate_empty</span> until the gate
              gets a condition or passes automatically.
            </Banner>
          )}
          <StatRow
            tiles={[
              { label: 'Templates', value: String(rows.length), icon: 'account_tree' },
              {
                label: 'Enabled, across organizations',
                value: String(enabled),
                icon: 'toggle_on',
              },
            ]}
          />
          <DataTable
            count={count(rows.length, 'template')}
            hint="open one here, or its canvas from the panel"
            rows={rows}
            rowKey="key"
            selectedKey={panel.current?.kind === 'linkage' ? panel.current.id : undefined}
            onRowClick={(t) => panel.open({ kind: 'linkage', id: t.key })}
            onRowOpenPage={(t) => openPage({ kind: 'linkage', id: t.key })}
            columns={[
              {
                key: 'name',
                label: 'Template',
                render: (t) => (
                  <span>
                    <strong style={{ display: 'block' }}>{t.name}</strong>
                    <span
                      className="mono"
                      style={{ fontSize: 'var(--text-label-lg)', color: 'var(--text-soft)' }}
                    >
                      {t.key}
                    </span>
                  </span>
                ),
              },
              {
                key: 'first_capability_id',
                label: 'Starts',
                wrap: true,
                render: (t) => {
                  const start = startWords(t.start, t.first_capability_id, timeZone);
                  return (
                    <span>
                      <span style={{ display: 'block' }}>
                        {isCall(t.start) && (
                          // The fork mark: its steps run beside the call (Feature 78 D8).
                          <span
                            aria-hidden
                            style={{
                              color: 'var(--info)',
                              verticalAlign: 'middle',
                              marginRight: 5,
                            }}
                          >
                            <Icon name="call_split" size={14} />
                          </span>
                        )}
                        {start.words}
                      </span>
                      {start.cron && (
                        <span
                          className="mono"
                          style={{ fontSize: 'var(--text-label-lg)', color: 'var(--text-soft)' }}
                        >
                          {start.cron}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: 'runs',
                label: 'Runs',
                mono: true,
                wrap: true,
                render: (t) => [...new Set(t.graph.steps.map((s) => s.capability_id))].join(', '),
              },
              {
                key: 'empty_gates',
                label: 'Gates',
                mono: true,
                render: (t) => {
                  const empty = t.empty_gates?.length ?? 0;
                  // A link out of a decision is a branch, not a gate (Feature 79).
                  const gates = t.graph.links.filter((l) => l.branch === undefined).length;
                  if (empty === 0) return `${gates} set`;
                  return (
                    <span style={{ color: 'var(--warn)', fontWeight: 'var(--weight-semibold)' }}>
                      {empty} empty
                    </span>
                  );
                },
              },
              {
                key: 'enabled_organizations',
                label: 'Organizations',
                align: 'right',
                mono: true,
                render: (t) =>
                  t.enabled_organizations === 0 ? '—' : String(t.enabled_organizations),
              },
              {
                key: 'overlaps',
                label: 'Flags',
                // A schedule wakes on no capability, so it never overlaps (D28); its
                // steps reach an organization only by webhook (D6, D10). A call
                // overlaps as a completion does, and its steps are delivered by
                // webhook too (Feature 78 D2, D9).
                render: (t) =>
                  isSchedule(t.start) ? (
                    <Badge tone="info">delivers by webhook</Badge>
                  ) : isCall(t.start) ? (
                    <Inline gap={5} align="center">
                      {overlapsOf(t.key).length > 0 && (
                        <OverlapBadge others={overlapsOf(t.key)} templates />
                      )}
                      <Badge tone="info">delivers by webhook</Badge>
                    </Inline>
                  ) : (
                    <OverlapBadge others={overlapsOf(t.key)} templates />
                  ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}
